'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Printer } from 'lucide-react';
import QRCode from 'react-qr-code';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PrintPortal } from '@/components/print-portal';
import { useSingleQrPrint } from '@/features/configurations';

interface RegistrationQrDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  containerNumber: string;
}

export function RegistrationQrDialog({
  open,
  onOpenChange,
  containerNumber
}: RegistrationQrDialogProps) {
  const t = useTranslations('registration');
  const { printerUid, printSingleQr } = useSingleQrPrint();
  const [printing, setPrinting] = useState(false);

  async function handlePrint() {
    if (!printerUid) {
      toast.message(t('setDefaultPrinter'));
      window.print();
      return;
    }
    setPrinting(true);
    const result = await printSingleQr({
      headerText: containerNumber,
      qrPayload: containerNumber
    });
    setPrinting(false);
    if (result.ok) {
      toast.success(t('printSentToZebra'));
    } else {
      toast.error(t('printFailedZebra', { reason: result.error }));
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='sm:max-w-sm'>
          <DialogHeader>
            <DialogTitle className='text-center'>
              {t('qrDialogTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className='bg-muted rounded-lg px-6 py-3 text-center'>
            <p className='text-muted-foreground mb-1 text-sm'>
              {t('registrationCode')}
            </p>
            <p className='text-foreground font-mono text-xl font-bold tracking-wider'>
              {containerNumber}
            </p>
          </div>

          <div className='flex justify-center py-2'>
            <div className='rounded-lg bg-white p-3'>
              <QRCode value={containerNumber} size={160} level='M' />
            </div>
          </div>

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              className='w-full'
              onClick={handlePrint}
              disabled={printing}
            >
              <Printer className='me-2 h-4 w-4' />
              {printing ? t('printing') : t('printCode')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrintPortal>
        <div className='hidden' data-print-zone>
          <div className='qr-sticker-grid'>
            <div className='qr-sticker'>
              <p
                style={{
                  marginBottom: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                  textAlign: 'center'
                }}
              >
                {containerNumber}
              </p>
              <QRCode value={containerNumber} size={100} level='M' />
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
